import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Complete translations
const translations = {
  fr: {
    // Navigation
    accueil: "Accueil",
    services: "Services",
    engagementRse: "Engagement RSE",
    aPropos: "À propos",
    postuler: "Postuler",
    contact: "Contact",
    langue: "Langue",
    // Common
    nousContacter: "Nous contacter",
    enSavoirPlus: "En savoir plus",
    voirTousServices: "Voir tous nos services",
    belgique: "Belgique",
    // Footer
    navigation: "Navigation",
    nosServices: "Nos Services",
    nosServicesMenu: "Nos Services",
    nettoyageBureaux: "Nettoyage de bureaux",
    nettoyageIndustriel: "Nettoyage industriel",
    entretienImmeubles: "Entretien d'immeubles",
    nettoyageApresTravaux: "Nettoyage après travaux",
    vitresSurfaces: "Vitres et surfaces vitrées",
    desinfection: "Désinfection",
    droitsReserves: "Tous droits réservés",
    politiqueConfidentialite: "Politique de confidentialité",
    mentionsLegales: "Mentions légales",
    footerDescription: "Votre partenaire de confiance pour tous vos besoins en nettoyage professionnel en Belgique. Expertise, rapidité, qualité et confiance.",
    // Hero
    garantitEnvironnement: "La garantie d'un environnement",
    propre: "propre",
    heroDescription: "Votre partenaire pour la qualité et la fiabilité. Services de nettoyage professionnels pour entreprises, industries et copropriétés en Belgique.",
    decouvrirServices: "Découvrir nos services",
    // Values
    expertise: "Expertise",
    expertiseDesc: "Équipes formées et certifiées",
    rapidite: "Rapidité",
    rapiditeDesc: "Intervention rapide garantie",
    qualite: "Qualité",
    qualiteDesc: "Standards de qualité élevés",
    confiance: "Confiance",
    confianceDesc: "Relation durable et transparente",
    // Services section
    nosExpertises: "Nos expertises",
    servicesNettoyagePro: "Services de nettoyage professionnel",
    servicesDescription: "Des solutions complètes et personnalisées pour tous vos besoins en propreté",
    bureauxLocaux: "Bureaux & Locaux",
    bureauxLocauxDesc: "Entretien complet de vos espaces professionnels",
    nettoyageIndustrielTitle: "Nettoyage Industriel",
    nettoyageIndustrielDesc: "Solutions adaptées aux environnements industriels",
    immeublesCopro: "Immeubles & Copropriétés",
    immeublesCoproDesc: "Entretien des parties communes",
    apresTravaux: "Après Travaux",
    apresTravauxDesc: "Remise en état post-chantier",
    vitresSurfacesTitle: "Vitres & Surfaces",
    vitresSurfacesDesc: "Nettoyage professionnel des vitrages",
    desinfectionTitle: "Désinfection",
    desinfectionDesc: "Services de désinfection spécialisés",
    // RSE section
    engagementEcologique: "Engagement écologique",
    acteurEcoResponsable: "Un acteur",
    ecoResponsable: "éco-responsable",
    rseDescription: "Chez Nettis, nous nous engageons pour un avenir durable. Nos pratiques respectent l'environnement tout en garantissant une qualité de service irréprochable.",
    rsePoint1: "Remplacement des produits actuels par des produits écologiques",
    rsePoint2: "Gestion raisonnée des ressources",
    rsePoint3: "Formation continue de nos équipes",
    rsePoint4: "Engagement pour le développement durable",
    decouvrirEngagement: "Découvrir notre engagement",
    produitsVerts: "Produits verts",
    // Stats
    anneesExperience: "Années d'expérience",
    clientsSatisfaits: "Clients satisfaits",
    disponibilite: "Disponibilité",
    reactifs: "Réactifs",
    engagementRseLabel: "Engagement RSE",
    // CTA
    pretCollaborer: "Prêt à collaborer avec nous ?",
    ctaDescription: "Contactez-nous pour discuter de vos besoins en nettoyage professionnel",
    rejoindreEquipe: "Rejoindre notre équipe",
    // Services Page
    servicesPageTitle: "Services de nettoyage",
    professionnel: "professionnel",
    servicesPageDesc: "Des solutions complètes et personnalisées pour tous vos besoins en propreté. Expertise, rapidité et qualité au service de votre environnement.",
    disponibilite247: "Réactifs",
    disponibilite247Desc: "Une équipe réactive prête à intervenir rapidement selon vos besoins",
    qualiteGarantie: "Qualité garantie",
    qualiteGarantieDesc: "Standards élevés et contrôle qualité rigoureux",
    equipesFormees: "Équipes formées",
    equipesFormeesDesc: "Personnel qualifié et certifié",
    besoinSpecifique: "Un besoin spécifique ?",
    besoinSpecifiqueDesc: "Contactez-nous pour discuter de vos besoins. Nous vous proposons une solution sur mesure adaptée à votre situation.",
    // Service details
    nettoyageBureauxTitle: "Nettoyage de Bureaux et Locaux Professionnels",
    nettoyageBureauxDesc: "Entretien complet de vos espaces de travail pour un environnement propre, sain et agréable.",
    nettoyageIndustrielFullTitle: "Nettoyage Industriel (Usines, Entrepôts)",
    nettoyageIndustrielFullDesc: "Intervention dans les environnements industriels avec le respect strict des normes de sécurité et d'hygiène.",
    entretienImmeublesTitle: "Entretien d'Immeubles et Copropriétés",
    entretienImmeublesDesc: "Nettoyage des parties communes pour un cadre agréable et valorisant.",
    nettoyageApresTravauxTitle: "Nettoyage Après Travaux / Après Sinistre",
    nettoyageApresTravauxDesc: "Nettoyage et désinfection complète après incendie ou dégât des eaux.",
    vitresSurfacesFullTitle: "Vitres et Surfaces Vitrées",
    vitresSurfacesFullDesc: "Techniciens spécialisés pour un résultat sans traces.",
    servicesSpecifiquesTitle: "Services Spécifiques (Désinfection, Remise en État)",
    servicesSpecifiquesDesc: "Prestations ciblées selon vos besoins spécifiques.",
    // RSE Page
    rsePageTitle: "Un acteur",
    valeursEcologiques: "Valeurs écologiques",
    quatrePiliers: "Nos 4 piliers environnementaux",
    piliersDec: "Une approche durable intégrée à chaque aspect de notre activité",
    responsabilite: "Responsabilité",
    responsabiliteDesc: "Nous assumons pleinement notre responsabilité environnementale dans chacune de nos interventions.",
    produitsVertsTitle: "Produits Verts",
    produitsVertsDesc: "Nous remplaçons progressivement nos produits par des alternatives écologiques respectueuses de l'environnement.",
    gestionRaisonnee: "Gestion Raisonnée",
    gestionRaisonneeDesc: "Optimisation des ressources et réduction des déchets pour un impact environnemental minimal.",
    engagementDurable: "Engagement Durable",
    engagementDurableDesc: "Vision à long terme pour contribuer à un avenir plus propre et plus durable.",
    engagementsConcrets: "Nos engagements concrets",
    engagementsConcretsDesc: "Des actions mesurables pour un impact réel",
    equipementsEconomes: "Équipements Économes",
    equipementsEconomesDesc: "Matériel à faible consommation énergétique",
    gestionDechets: "Gestion des Déchets",
    gestionDechetsDesc: "Tri sélectif et recyclage systématique",
    formationContinue: "Formation Continue",
    formationContinueDesc: "Personnel formé aux pratiques durables",
    responsabiliteSociale: "Responsabilité sociale",
    engagementsSociaux: "Nos engagements sociaux",
    engagementsSociauxDesc: "Une entreprise juste et humaine au service de ses collaborateurs et de la société",
    entrepriseJuste: "Entreprise Juste et Humaine",
    entrepriseJusteDesc: "Lutte contre la discrimination, égalité des chances, respect et bienveillance envers tous nos collaborateurs.",
    integrite: "Intégrité et Transparence",
    integriteDesc: "Rejet total de la corruption, extorsion et fraude. Respect des lois et de l'éthique professionnelle.",
    concurrenceLoyale: "Concurrence Loyale",
    concurrenceLoyaleDesc: "Respect des règles du marché et équité dans toutes nos relations commerciales.",
    engagementSocietal: "Engagement Sociétal",
    engagementSocietalDesc: "Contribution active au développement local et social de nos communautés.",
    confidentialite: "Confidentialité et Protection des Données",
    confidentialiteDesc: "La sécurité des informations qui nous sont confiées est une priorité absolue.",
    proprieteIntellectuelle: "Respect de la Propriété Intellectuelle",
    proprieteIntellectuelleDesc: "Nous protégeons les savoir-faire, innovations et documents professionnels de nos clients et partenaires.",
    rejoignezDemarche: "Rejoignez notre démarche responsable",
    rejoignezDemarcheDesc: "Choisir Nettis, c'est faire le choix d'un partenaire engagé pour un avenir plus propre et plus durable.",
    // About Page
    aProposTitle: "Votre partenaire pour la",
    qualiteEt: "qualité",
    fiabilite: "fiabilité",
    aProposDesc: "Nettis Facility est une entreprise belge spécialisée dans les services de nettoyage professionnel.",
    annees: "Années",
    clients: "Clients",
    satisfaction: "Satisfaction client",
    notreVision: "Notre vision",
    redefinirStandards: "Redéfinir les standards de propreté",
    missionDesc: "Notre mission est de garantir un environnement propre, sain et agréable pour tous nos clients.",
    notreMission: "Notre Mission",
    notreMissionDesc: "Offrir des services de nettoyage de qualité supérieure tout en préservant l'environnement.",
    notreVisionTitle: "Notre Vision",
    notreVisionDesc: "Devenir la référence en Belgique pour les services de nettoyage éco-responsables.",
    nosAtouts: "Nos atouts",
    pourquoiNousChoisir: "Pourquoi nous choisir ?",
    atout1: "Expertise de plus de 15 ans dans le secteur",
    atout2: "Équipes formées et certifiées",
    atout3: "Interlocuteur dédié pour chaque client",
    atout4: "Offres gratuites et transparentes",
    atout5: "Couverture nationale en Belgique",
    atout6: "Engagement qualité et RSE",
    notreApproche: "Notre approche",
    notreMethodologie: "Notre méthodologie",
    methodologieDesc: "Un processus éprouvé pour garantir votre satisfaction à chaque étape",
    etape1: "Prise de contact",
    etape1Desc: "Premier échange pour comprendre vos besoins et attentes spécifiques.",
    etape2: "Analyse des besoins",
    etape2Desc: "Visite de vos locaux et évaluation précise des prestations nécessaires.",
    etape3: "Proposition personnalisée",
    etape3Desc: "Offre détaillée et transparente, adaptée à votre budget et vos exigences.",
    etape4: "Mise en œuvre",
    etape4Desc: "Déploiement de nos équipes avec un interlocuteur dédié à votre service.",
    etape5: "Suivi qualité",
    etape5Desc: "Contrôles réguliers et ajustements pour garantir votre satisfaction.",
    zoneIntervention: "Zone d'intervention",
    couvertureNationale: "Couverture nationale en Belgique",
    couvertureDesc: "Basés à Bruxelles, nous intervenons dans toute la Belgique.",
    bruxellesCapitale: "Bruxelles-Capitale",
    bruxellesDesc: "Siège social et présence forte",
    wallonie: "Wallonie",
    wallonieDesc: "Couverture complète de la région",
    flandre: "Flandre",
    flandreDesc: "Interventions sur demande",
    nosCoordonnees: "Nos coordonnées",
    adresse: "Adresse",
    telephone: "Téléphone",
    email: "Email",
    pretTravailler: "Prêt à travailler avec nous ?",
    pretTravaillerDesc: "Contactez-nous pour discuter de vos besoins en nettoyage professionnel.",
    // Contact Page
    contactTitle: "Contactez-",
    nous: "nous",
    contactDesc: "Une question, un projet ? Notre équipe est à votre disposition pour vous accompagner dans tous vos besoins en nettoyage professionnel.",
    envoyezMessage: "Envoyez-nous un message",
    nomComplet: "Nom complet",
    votreMail: "votre@email.com",
    votreMessage: "Comment pouvons-nous vous aider ?",
    message: "Message",
    envoyerMessage: "Envoyer le message",
    envoiEnCours: "Envoi en cours...",
    messageEnvoye: "Message envoyé !",
    merciContact: "Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.",
    envoyerAutre: "Envoyer un autre message",
    champsObligatoires: "Champs obligatoires",
    contactParDepartement: "Contacts par département",
    serviceComptable: "Service Comptable",
    ressourcesHumaines: "Ressources Humaines",
    serviceCommercial: "Service Commercial",
    rejoindreEquipeQuestion: "Vous souhaitez rejoindre notre équipe ?",
    rejoindreEquipeDesc: "Nous sommes toujours à la recherche de talents motivés pour renforcer nos équipes.",
    postulerChezNettis: "Postuler chez Nettis",
    // Postuler Page
    rejoignezNous: "Rejoignez-nous",
    postulerTitle: "Postulez chez",
    postulerDesc: "Rejoignez une entreprise dynamique et humaine. Nous sommes toujours à la recherche de talents motivés pour renforcer nos équipes.",
    horairesFlexibles: "Horaires flexibles",
    horairesFlexiblesDesc: "Adaptés à votre vie personnelle",
    formationContinueAvantage: "Formation continue",
    formationContinueAvantageDesc: "Développez vos compétences",
    equipeBienveillante: "Équipe bienveillante",
    equipeBienveillanteDesc: "Un environnement humain",
    proximite: "Proximité",
    proximiteDesc: "Postes partout en Belgique",
    formulaireCandidature: "Formulaire de candidature",
    formulaireDesc: "Remplissez le formulaire ci-dessous pour postuler chez Nettis Facility.",
    nom: "Nom",
    prenom: "Prénom",
    votreNom: "Votre nom",
    votrePrenom: "Votre prénom",
    votreMessageCandidature: "Présentez-vous brièvement, indiquez le poste souhaité, votre expérience...",
    documentsOptionnels: "Documents (optionnel)",
    formatsAcceptes: "Formats acceptés : PDF, JPG, PNG, DOC, DOCX (max. 5 Mo par fichier)",
    cv: "CV (Curriculum Vitae)",
    lettreMotivation: "Lettre de motivation",
    carteIdentite: "Carte d'identité (recto/verso)",
    photoIdentite: "Photo d'identité",
    planActiva: "Plan Activa (si applicable)",
    choisirFichier: "Choisir un fichier",
    supprimer: "Supprimer",
    protectionDonnees: "Protection des données (RGPD)",
    responsableTraitement: "Responsable du traitement",
    finalite: "Finalité",
    finaliteDesc: "Vos données sont collectées uniquement dans le cadre du processus de recrutement.",
    conservation: "Conservation",
    conservationDesc: "Vos données seront conservées pendant une durée maximale de 2 ans après votre dernière candidature, conformément à la législation belge.",
    vosDroits: "Vos droits",
    vosDroitsDesc: "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation et de portabilité de vos données.",
    reclamation: "Réclamation",
    reclamationDesc: "Vous pouvez introduire une réclamation auprès de l'Autorité de protection des données (APD)",
    consentementRgpd: "J'ai lu et j'accepte la politique de confidentialité. Je consens au traitement de mes données personnelles et documents dans le cadre de ma candidature chez Nettis Facility.",
    envoyerCandidature: "Envoyer ma candidature",
    candidatureEnvoyee: "Candidature envoyée !",
    merciCandidature: "Merci pour votre intérêt. Notre équipe RH examinera votre candidature et vous recontactera dans les meilleurs délais.",
    soumettreAutre: "Soumettre une autre candidature",
    questionRecrutement: "Une question sur le recrutement ?",
    equipeRhDisposition: "Notre équipe RH est à votre disposition pour répondre à toutes vos questions.",
    // Services Page - Features
    serviceFeature1_1: "Nettoyage quotidien ou hebdomadaire",
    serviceFeature1_2: "Désinfection des surfaces de contact",
    serviceFeature1_3: "Entretien des sols et moquettes",
    serviceFeature1_4: "Vidage des poubelles et recyclage",
    serviceFeature1_5: "Nettoyage des sanitaires",
    serviceFeature2_1: "Nettoyage des zones de production",
    serviceFeature2_2: "Dégraissage des équipements",
    serviceFeature2_3: "Entretien des sols industriels",
    serviceFeature2_4: "Nettoyage haute pression",
    serviceFeature2_5: "Gestion des déchets industriels",
    serviceFeature3_1: "Entretien des halls d'entrée",
    serviceFeature3_2: "Nettoyage des escaliers et ascenseurs",
    serviceFeature3_3: "Entretien des espaces extérieurs",
    serviceFeature3_4: "Sortie et rentrée des poubelles",
    serviceFeature3_5: "Entretien des locaux techniques",
    serviceFeature4_1: "Élimination des gravats et débris",
    serviceFeature4_2: "Nettoyage des poussières fines",
    serviceFeature4_3: "Remise en état des surfaces",
    serviceFeature4_4: "Désinfection complète",
    serviceFeature4_5: "Traitement anti-odeurs",
    serviceFeature5_1: "Nettoyage intérieur et extérieur",
    serviceFeature5_2: "Travaux en hauteur sécurisés",
    serviceFeature5_3: "Traitement anti-calcaire",
    serviceFeature5_4: "Nettoyage des châssis",
    serviceFeature5_5: "Contrats d'entretien régulier",
    serviceFeature6_1: "Désinfection virucide et bactéricide",
    serviceFeature6_2: "Traitement des sols spéciaux",
    serviceFeature6_3: "Nettoyage de fin de bail",
    serviceFeature6_4: "Shampouinage de moquettes",
    serviceFeature6_5: "Cristallisation des marbres",
    // RSE Page - Social Commitment Points
    rsePoint1_1: "Égalité professionnelle femmes/hommes",
    rsePoint1_2: "Inclusion de tous horizons",
    rsePoint1_3: "Valorisation du mérite",
    rsePoint1_4: "Formation continue",
    rsePoint2_1: "Pratiques commerciales éthiques",
    rsePoint2_2: "Transparence des tarifs",
    rsePoint2_3: "Communication honnête",
    rsePoint2_4: "Respect des engagements",
    rsePoint3_1: "Prix justes et transparents",
    rsePoint3_2: "Respect des partenaires",
    rsePoint3_3: "Relations équitables",
    rsePoint3_4: "Éthique des affaires",
    rsePoint4_1: "Promotion de l'emploi local",
    rsePoint4_2: "Partenariats responsables",
    rsePoint4_3: "Soutien aux initiatives locales",
    rsePoint4_4: "Insertion professionnelle",
    // RSE Page - Confidentiality Points
    rseConfid1: "Protection des données personnelles",
    rseConfid2: "Clauses de confidentialité",
    rseConfid3: "Sécurité des accès",
    rseConfid4: "Formation du personnel",
    // RSE Page - Intellectual Property Points
    rsePropInt1: "Protection des innovations",
    rsePropInt2: "Respect des brevets",
    rsePropInt3: "Confidentialité des procédés",
    rsePropInt4: "Éthique professionnelle",
    // Cookie Consent
    cookieTitle: "Nous utilisons des cookies",
    cookieDescription: "Nous utilisons des cookies pour améliorer votre expérience sur notre site et analyser notre trafic. Vous pouvez choisir d'accepter tous les cookies ou de personnaliser vos préférences.",
    cookieSettings: "Paramètres des cookies",
    cookieNecessary: "Cookies nécessaires",
    cookieNecessaryDesc: "Ces cookies sont essentiels au fonctionnement du site.",
    cookieAnalytics: "Cookies analytiques",
    cookieAnalyticsDesc: "Ces cookies nous aident à comprendre comment vous utilisez notre site.",
    cookieAlwaysActive: "Toujours actif",
    cookieAcceptAll: "Accepter tout",
    cookieRejectNonEssential: "Refuser les non-essentiels",
    cookieCustomize: "Personnaliser",
    cookieSavePreferences: "Sauvegarder"
  },
  en: {
    // Navigation
    accueil: "Home",
    services: "Services",
    engagementRse: "CSR Commitment",
    aPropos: "About",
    postuler: "Careers",
    contact: "Contact",
    langue: "Language",
    // Common
    nousContacter: "Contact us",
    enSavoirPlus: "Learn more",
    voirTousServices: "View all our services",
    belgique: "Belgium",
    // Footer
    navigation: "Navigation",
    nosServices: "Our Services",
    nosServicesMenu: "Our Services",
    nettoyageBureaux: "Office cleaning",
    nettoyageIndustriel: "Industrial cleaning",
    entretienImmeubles: "Building maintenance",
    nettoyageApresTravaux: "Post-construction cleaning",
    vitresSurfaces: "Windows and glass surfaces",
    desinfection: "Disinfection",
    droitsReserves: "All rights reserved",
    politiqueConfidentialite: "Privacy policy",
    mentionsLegales: "Legal notice",
    footerDescription: "Your trusted partner for all your professional cleaning needs in Belgium. Expertise, speed, quality and trust.",
    // Hero
    garantitEnvironnement: "The guarantee of a clean",
    propre: "environment",
    heroDescription: "Your partner for quality and reliability. Professional cleaning services for businesses, industries and condominiums in Belgium.",
    decouvrirServices: "Discover our services",
    // Values
    expertise: "Expertise",
    expertiseDesc: "Trained and certified teams",
    rapidite: "Speed",
    rapiditeDesc: "Guaranteed quick response",
    qualite: "Quality",
    qualiteDesc: "High quality standards",
    confiance: "Trust",
    confianceDesc: "Long-lasting and transparent relationship",
    // Services section
    nosExpertises: "Our expertise",
    servicesNettoyagePro: "Professional cleaning services",
    servicesDescription: "Complete and customized solutions for all your cleaning needs",
    bureauxLocaux: "Offices & Premises",
    bureauxLocauxDesc: "Complete maintenance of your professional spaces",
    nettoyageIndustrielTitle: "Industrial Cleaning",
    nettoyageIndustrielDesc: "Solutions adapted to industrial environments",
    immeublesCopro: "Buildings & Condominiums",
    immeublesCoproDesc: "Maintenance of common areas",
    apresTravaux: "Post-Construction",
    apresTravauxDesc: "Post-construction restoration",
    vitresSurfacesTitle: "Windows & Surfaces",
    vitresSurfacesDesc: "Professional window cleaning",
    desinfectionTitle: "Disinfection",
    desinfectionDesc: "Specialized disinfection services",
    // RSE section
    engagementEcologique: "Environmental commitment",
    acteurEcoResponsable: "An",
    ecoResponsable: "eco-responsible actor",
    rseDescription: "At Nettis, we are committed to a sustainable future. Our practices respect the environment while guaranteeing impeccable service quality.",
    rsePoint1: "Replacing current products with ecological products",
    rsePoint2: "Rational resource management",
    rsePoint3: "Continuous training of our teams",
    rsePoint4: "Commitment to sustainable development",
    decouvrirEngagement: "Discover our commitment",
    produitsVerts: "Green products",
    // Stats
    anneesExperience: "Years of experience",
    clientsSatisfaits: "Satisfied clients",
    disponibilite: "Availability",
    reactifs: "Responsive",
    engagementRseLabel: "CSR Commitment",
    // CTA
    pretCollaborer: "Ready to work with us?",
    ctaDescription: "Contact us to discuss your professional cleaning needs",
    rejoindreEquipe: "Join our team",
    // Services Page
    servicesPageTitle: "Professional cleaning",
    professionnel: "services",
    servicesPageDesc: "Complete and customized solutions for all your cleaning needs. Expertise, speed and quality at the service of your environment.",
    disponibilite247: "Responsive",
    disponibilite247Desc: "A responsive team ready to intervene quickly according to your needs",
    qualiteGarantie: "Guaranteed quality",
    qualiteGarantieDesc: "High standards and rigorous quality control",
    equipesFormees: "Trained teams",
    equipesFormeesDesc: "Qualified and certified staff",
    besoinSpecifique: "A specific need?",
    besoinSpecifiqueDesc: "Contact us to discuss your needs. We offer a tailor-made solution adapted to your situation.",
    // Service details
    nettoyageBureauxTitle: "Office and Professional Premises Cleaning",
    nettoyageBureauxDesc: "Complete maintenance of your workspaces for a clean, healthy and pleasant environment.",
    nettoyageIndustrielFullTitle: "Industrial Cleaning (Factories, Warehouses)",
    nettoyageIndustrielFullDesc: "Intervention in industrial environments with strict compliance with safety and hygiene standards.",
    entretienImmeublesTitle: "Building and Condominium Maintenance",
    entretienImmeublesDesc: "Cleaning of common areas for a pleasant and valuable setting.",
    nettoyageApresTravauxTitle: "Post-Construction / Post-Disaster Cleaning",
    nettoyageApresTravauxDesc: "Complete cleaning and disinfection after fire or water damage.",
    vitresSurfacesFullTitle: "Windows and Glass Surfaces",
    vitresSurfacesFullDesc: "Specialized technicians for streak-free results.",
    servicesSpecifiquesTitle: "Specific Services (Disinfection, Restoration)",
    servicesSpecifiquesDesc: "Targeted services according to your specific needs.",
    // RSE Page
    rsePageTitle: "An",
    valeursEcologiques: "Ecological values",
    quatrePiliers: "Our 4 environmental pillars",
    piliersDec: "A sustainable approach integrated into every aspect of our activity",
    responsabilite: "Responsibility",
    responsabiliteDesc: "We fully assume our environmental responsibility in each of our interventions.",
    produitsVertsTitle: "Green Products",
    produitsVertsDesc: "We are progressively replacing our products with environmentally friendly alternatives.",
    gestionRaisonnee: "Rational Management",
    gestionRaisonneeDesc: "Resource optimization and waste reduction for minimal environmental impact.",
    engagementDurable: "Sustainable Commitment",
    engagementDurableDesc: "Long-term vision to contribute to a cleaner and more sustainable future.",
    engagementsConcrets: "Our concrete commitments",
    engagementsConcretsDesc: "Measurable actions for real impact",
    equipementsEconomes: "Energy-Efficient Equipment",
    equipementsEconomesDesc: "Low energy consumption equipment",
    gestionDechets: "Waste Management",
    gestionDechetsDesc: "Selective sorting and systematic recycling",
    formationContinue: "Continuous Training",
    formationContinueDesc: "Staff trained in sustainable practices",
    responsabiliteSociale: "Social responsibility",
    engagementsSociaux: "Our social commitments",
    engagementsSociauxDesc: "A fair and human company serving its employees and society",
    entrepriseJuste: "Fair and Human Company",
    entrepriseJusteDesc: "Fight against discrimination, equal opportunities, respect and kindness towards all our employees.",
    integrite: "Integrity and Transparency",
    integriteDesc: "Total rejection of corruption, extortion and fraud. Respect for laws and professional ethics.",
    concurrenceLoyale: "Fair Competition",
    concurrenceLoyaleDesc: "Respect for market rules and fairness in all our business relationships.",
    engagementSocietal: "Social Commitment",
    engagementSocietalDesc: "Active contribution to the local and social development of our communities.",
    confidentialite: "Confidentiality and Data Protection",
    confidentialiteDesc: "The security of information entrusted to us is an absolute priority.",
    proprieteIntellectuelle: "Respect for Intellectual Property",
    proprieteIntellectuelleDesc: "We protect the know-how, innovations and professional documents of our clients and partners.",
    rejoignezDemarche: "Join our responsible approach",
    rejoignezDemarcheDesc: "Choosing Nettis means choosing a partner committed to a cleaner and more sustainable future.",
    // About Page
    aProposTitle: "Your partner for",
    qualiteEt: "quality",
    fiabilite: "reliability",
    aProposDesc: "Nettis Facility is a Belgian company specialized in professional cleaning services.",
    annees: "Years",
    clients: "Clients",
    satisfaction: "Client satisfaction",
    notreVision: "Our vision",
    redefinirStandards: "Redefining cleanliness standards",
    missionDesc: "Our mission is to guarantee a clean, healthy and pleasant environment for all our clients.",
    notreMission: "Our Mission",
    notreMissionDesc: "Offer superior quality cleaning services while preserving the environment.",
    notreVisionTitle: "Our Vision",
    notreVisionDesc: "Become the reference in Belgium for eco-responsible cleaning services.",
    nosAtouts: "Our assets",
    pourquoiNousChoisir: "Why choose us?",
    atout1: "Over 15 years of expertise in the sector",
    atout2: "Trained and certified teams",
    atout3: "Dedicated contact for each client",
    atout4: "Free and transparent quotes",
    atout5: "National coverage in Belgium",
    atout6: "Quality and CSR commitment",
    notreApproche: "Our approach",
    notreMethodologie: "Our methodology",
    methodologieDesc: "A proven process to guarantee your satisfaction at every step",
    etape1: "First contact",
    etape1Desc: "Initial exchange to understand your specific needs and expectations.",
    etape2: "Needs analysis",
    etape2Desc: "Visit to your premises and precise evaluation of the necessary services.",
    etape3: "Personalized proposal",
    etape3Desc: "Detailed and transparent offer, adapted to your budget and requirements.",
    etape4: "Implementation",
    etape4Desc: "Deployment of our teams with a dedicated contact at your service.",
    etape5: "Quality monitoring",
    etape5Desc: "Regular checks and adjustments to guarantee your satisfaction.",
    zoneIntervention: "Intervention area",
    couvertureNationale: "National coverage in Belgium",
    couvertureDesc: "Based in Brussels, we operate throughout Belgium.",
    bruxellesCapitale: "Brussels-Capital",
    bruxellesDesc: "Headquarters and strong presence",
    wallonie: "Wallonia",
    wallonieDesc: "Complete coverage of the region",
    flandre: "Flanders",
    flandreDesc: "Interventions on request",
    nosCoordonnees: "Our contact details",
    adresse: "Address",
    telephone: "Phone",
    email: "Email",
    pretTravailler: "Ready to work with us?",
    pretTravaillerDesc: "Contact us to discuss your professional cleaning needs.",
    // Contact Page
    contactTitle: "Contact",
    nous: "us",
    contactDesc: "A question, a project? Our team is at your disposal to assist you with all your professional cleaning needs.",
    envoyezMessage: "Send us a message",
    nomComplet: "Full name",
    votreMail: "your@email.com",
    votreMessage: "How can we help you?",
    message: "Message",
    envoyerMessage: "Send message",
    envoiEnCours: "Sending...",
    messageEnvoye: "Message sent!",
    merciContact: "Thank you for contacting us. We will respond as soon as possible.",
    envoyerAutre: "Send another message",
    champsObligatoires: "Required fields",
    contactParDepartement: "Contact by department",
    serviceComptable: "Accounting Department",
    ressourcesHumaines: "Human Resources",
    serviceCommercial: "Sales Department",
    rejoindreEquipeQuestion: "Want to join our team?",
    rejoindreEquipeDesc: "We are always looking for motivated talents to strengthen our teams.",
    postulerChezNettis: "Apply at Nettis",
    // Postuler Page
    rejoignezNous: "Join us",
    postulerTitle: "Apply at",
    postulerDesc: "Join a dynamic and human company. We are always looking for motivated talents to strengthen our teams.",
    horairesFlexibles: "Flexible hours",
    horairesFlexiblesDesc: "Adapted to your personal life",
    formationContinueAvantage: "Continuous training",
    formationContinueAvantageDesc: "Develop your skills",
    equipeBienveillante: "Caring team",
    equipeBienveillanteDesc: "A human environment",
    proximite: "Proximity",
    proximiteDesc: "Positions throughout Belgium",
    formulaireCandidature: "Application form",
    formulaireDesc: "Fill out the form below to apply at Nettis Facility.",
    nom: "Last name",
    prenom: "First name",
    votreNom: "Your last name",
    votrePrenom: "Your first name",
    votreMessageCandidature: "Briefly introduce yourself, indicate the desired position, your experience...",
    documentsOptionnels: "Documents (optional)",
    formatsAcceptes: "Accepted formats: PDF, JPG, PNG, DOC, DOCX (max. 5 MB per file)",
    cv: "CV (Resume)",
    lettreMotivation: "Cover letter",
    carteIdentite: "ID card (front/back)",
    photoIdentite: "ID photo",
    planActiva: "Activa Plan (if applicable)",
    choisirFichier: "Choose a file",
    supprimer: "Delete",
    protectionDonnees: "Data Protection (GDPR)",
    responsableTraitement: "Data controller",
    finalite: "Purpose",
    finaliteDesc: "Your data is collected solely for the recruitment process.",
    conservation: "Retention",
    conservationDesc: "Your data will be kept for a maximum of 2 years after your last application, in accordance with Belgian law.",
    vosDroits: "Your rights",
    vosDroitsDesc: "You have the right to access, rectify, delete, limit and port your data.",
    reclamation: "Complaint",
    reclamationDesc: "You can file a complaint with the Data Protection Authority (DPA)",
    consentementRgpd: "I have read and accept the privacy policy. I consent to the processing of my personal data and documents as part of my application at Nettis Facility.",
    envoyerCandidature: "Submit my application",
    candidatureEnvoyee: "Application submitted!",
    merciCandidature: "Thank you for your interest. Our HR team will review your application and get back to you as soon as possible.",
    soumettreAutre: "Submit another application",
    questionRecrutement: "Questions about recruitment?",
    equipeRhDisposition: "Our HR team is at your disposal to answer all your questions.",
    // Services Page - Features
    serviceFeature1_1: "Daily or weekly cleaning",
    serviceFeature1_2: "Disinfection of contact surfaces",
    serviceFeature1_3: "Floor and carpet maintenance",
    serviceFeature1_4: "Bin emptying and recycling",
    serviceFeature1_5: "Sanitary cleaning",
    serviceFeature2_1: "Production area cleaning",
    serviceFeature2_2: "Equipment degreasing",
    serviceFeature2_3: "Industrial floor maintenance",
    serviceFeature2_4: "High pressure cleaning",
    serviceFeature2_5: "Industrial waste management",
    serviceFeature3_1: "Entrance hall maintenance",
    serviceFeature3_2: "Staircase and elevator cleaning",
    serviceFeature3_3: "Outdoor area maintenance",
    serviceFeature3_4: "Bin collection and return",
    serviceFeature3_5: "Technical room maintenance",
    serviceFeature4_1: "Rubble and debris removal",
    serviceFeature4_2: "Fine dust cleaning",
    serviceFeature4_3: "Surface restoration",
    serviceFeature4_4: "Complete disinfection",
    serviceFeature4_5: "Odour treatment",
    serviceFeature5_1: "Interior and exterior cleaning",
    serviceFeature5_2: "Safe work at height",
    serviceFeature5_3: "Anti-limescale treatment",
    serviceFeature5_4: "Frame cleaning",
    serviceFeature5_5: "Regular maintenance contracts",
    serviceFeature6_1: "Virucidal and bactericidal disinfection",
    serviceFeature6_2: "Special floor treatment",
    serviceFeature6_3: "End of lease cleaning",
    serviceFeature6_4: "Carpet shampooing",
    serviceFeature6_5: "Marble crystallization",
    // RSE Page - Social Commitment Points
    rsePoint1_1: "Gender equality in the workplace",
    rsePoint1_2: "Inclusion from all backgrounds",
    rsePoint1_3: "Merit recognition",
    rsePoint1_4: "Continuous training",
    rsePoint2_1: "Ethical business practices",
    rsePoint2_2: "Price transparency",
    rsePoint2_3: "Honest communication",
    rsePoint2_4: "Commitment respect",
    rsePoint3_1: "Fair and transparent prices",
    rsePoint3_2: "Partner respect",
    rsePoint3_3: "Fair relationships",
    rsePoint3_4: "Business ethics",
    rsePoint4_1: "Local employment promotion",
    rsePoint4_2: "Responsible partnerships",
    rsePoint4_3: "Support for local initiatives",
    rsePoint4_4: "Professional integration",
    // RSE Page - Confidentiality Points
    rseConfid1: "Personal data protection",
    rseConfid2: "Confidentiality clauses",
    rseConfid3: "Access security",
    rseConfid4: "Staff training",
    // RSE Page - Intellectual Property Points
    rsePropInt1: "Innovation protection",
    rsePropInt2: "Patent respect",
    rsePropInt3: "Process confidentiality",
    rsePropInt4: "Professional ethics",
    // Cookie Consent
    cookieTitle: "We use cookies",
    cookieDescription: "We use cookies to improve your experience on our site and analyze our traffic. You can choose to accept all cookies or customize your preferences.",
    cookieSettings: "Cookie settings",
    cookieNecessary: "Necessary cookies",
    cookieNecessaryDesc: "These cookies are essential for the website to function.",
    cookieAnalytics: "Analytics cookies",
    cookieAnalyticsDesc: "These cookies help us understand how you use our site.",
    cookieAlwaysActive: "Always active",
    cookieAcceptAll: "Accept all",
    cookieRejectNonEssential: "Reject non-essential",
    cookieCustomize: "Customize",
    cookieSavePreferences: "Save"
  },
  nl: {
    // Navigation
    accueil: "Home",
    services: "Diensten",
    engagementRse: "MVO Engagement",
    aPropos: "Over ons",
    postuler: "Vacatures",
    contact: "Contact",
    langue: "Taal",
    // Common
    nousContacter: "Contacteer ons",
    enSavoirPlus: "Meer informatie",
    voirTousServices: "Bekijk al onze diensten",
    belgique: "België",
    // Footer
    navigation: "Navigatie",
    nosServices: "Onze Diensten",
    nosServicesMenu: "Onze Diensten",
    nettoyageBureaux: "Kantoor schoonmaak",
    nettoyageIndustriel: "Industriële reiniging",
    entretienImmeubles: "Gebouwonderhoud",
    nettoyageApresTravaux: "Schoonmaak na werken",
    vitresSurfaces: "Ramen en glazen oppervlakken",
    desinfection: "Desinfectie",
    droitsReserves: "Alle rechten voorbehouden",
    politiqueConfidentialite: "Privacybeleid",
    mentionsLegales: "Juridische kennisgeving",
    footerDescription: "Uw betrouwbare partner voor al uw professionele schoonmaakbehoeften in België. Expertise, snelheid, kwaliteit en vertrouwen.",
    // Hero
    garantitEnvironnement: "De garantie van een schone",
    propre: "omgeving",
    heroDescription: "Uw partner voor kwaliteit en betrouwbaarheid. Professionele schoonmaakdiensten voor bedrijven, industrieën en mede-eigendommen in België.",
    decouvrirServices: "Ontdek onze diensten",
    // Values
    expertise: "Expertise",
    expertiseDesc: "Opgeleide en gecertificeerde teams",
    rapidite: "Snelheid",
    rapiditeDesc: "Gegarandeerde snelle reactie",
    qualite: "Kwaliteit",
    qualiteDesc: "Hoge kwaliteitsnormen",
    confiance: "Vertrouwen",
    confianceDesc: "Duurzame en transparante relatie",
    // Services section
    nosExpertises: "Onze expertise",
    servicesNettoyagePro: "Professionele schoonmaakdiensten",
    servicesDescription: "Complete en op maat gemaakte oplossingen voor al uw schoonmaakbehoeften",
    bureauxLocaux: "Kantoren & Ruimtes",
    bureauxLocauxDesc: "Volledig onderhoud van uw professionele ruimtes",
    nettoyageIndustrielTitle: "Industriële Reiniging",
    nettoyageIndustrielDesc: "Oplossingen aangepast aan industriële omgevingen",
    immeublesCopro: "Gebouwen & Appartementen",
    immeublesCoproDesc: "Onderhoud van gemeenschappelijke ruimtes",
    apresTravaux: "Na Werken",
    apresTravauxDesc: "Herstel na bouwwerkzaamheden",
    vitresSurfacesTitle: "Ramen & Oppervlakken",
    vitresSurfacesDesc: "Professionele glazenwassing",
    desinfectionTitle: "Desinfectie",
    desinfectionDesc: "Gespecialiseerde desinfectiediensten",
    // RSE section
    engagementEcologique: "Milieu-engagement",
    acteurEcoResponsable: "Een",
    ecoResponsable: "milieuvriendelijke speler",
    rseDescription: "Bij Nettis zetten we ons in voor een duurzame toekomst. Onze praktijken respecteren het milieu en garanderen tegelijkertijd een onberispelijke servicekwaliteit.",
    rsePoint1: "Vervanging van huidige producten door ecologische producten",
    rsePoint2: "Rationeel beheer van middelen",
    rsePoint3: "Continue opleiding van onze teams",
    rsePoint4: "Engagement voor duurzame ontwikkeling",
    decouvrirEngagement: "Ontdek ons engagement",
    produitsVerts: "Groene producten",
    // Stats
    anneesExperience: "Jaren ervaring",
    clientsSatisfaits: "Tevreden klanten",
    disponibilite: "Beschikbaarheid",
    reactifs: "Reactief",
    engagementRseLabel: "MVO Engagement",
    // CTA
    pretCollaborer: "Klaar om met ons samen te werken?",
    ctaDescription: "Neem contact met ons op om uw professionele schoonmaakbehoeften te bespreken",
    rejoindreEquipe: "Word lid van ons team",
    // Services Page
    servicesPageTitle: "Professionele",
    professionnel: "schoonmaakdiensten",
    servicesPageDesc: "Complete en op maat gemaakte oplossingen voor al uw schoonmaakbehoeften. Expertise, snelheid en kwaliteit ten dienste van uw omgeving.",
    disponibilite247: "Reactief",
    disponibilite247Desc: "Een reactief team klaar om snel in te grijpen volgens uw behoeften",
    qualiteGarantie: "Gegarandeerde kwaliteit",
    qualiteGarantieDesc: "Hoge normen en strenge kwaliteitscontrole",
    equipesFormees: "Opgeleide teams",
    equipesFormeesDesc: "Gekwalificeerd en gecertificeerd personeel",
    besoinSpecifique: "Een specifieke behoefte?",
    besoinSpecifiqueDesc: "Neem contact met ons op om uw behoeften te bespreken. Wij bieden een op maat gemaakte oplossing aangepast aan uw situatie.",
    // Service details
    nettoyageBureauxTitle: "Schoonmaak van Kantoren en Professionele Ruimtes",
    nettoyageBureauxDesc: "Volledig onderhoud van uw werkruimtes voor een schone, gezonde en aangename omgeving.",
    nettoyageIndustrielFullTitle: "Industriële Schoonmaak (Fabrieken, Magazijnen)",
    nettoyageIndustrielFullDesc: "Interventie in industriële omgevingen met strikte naleving van veiligheids- en hygiënenormen.",
    entretienImmeublesTitle: "Onderhoud van Gebouwen en Appartementen",
    entretienImmeublesDesc: "Schoonmaak van gemeenschappelijke ruimtes voor een aangename en waardevolle omgeving.",
    nettoyageApresTravauxTitle: "Schoonmaak Na Werken / Na Schade",
    nettoyageApresTravauxDesc: "Volledige schoonmaak en desinfectie na brand of waterschade.",
    vitresSurfacesFullTitle: "Ramen en Glazen Oppervlakken",
    vitresSurfacesFullDesc: "Gespecialiseerde technici voor streepvrije resultaten.",
    servicesSpecifiquesTitle: "Specifieke Diensten (Desinfectie, Herstel)",
    servicesSpecifiquesDesc: "Gerichte diensten volgens uw specifieke behoeften.",
    // RSE Page
    rsePageTitle: "Een",
    valeursEcologiques: "Ecologische waarden",
    quatrePiliers: "Onze 4 milieupijlers",
    piliersDec: "Een duurzame aanpak geïntegreerd in elk aspect van onze activiteit",
    responsabilite: "Verantwoordelijkheid",
    responsabiliteDesc: "Wij nemen onze milieu-verantwoordelijkheid volledig op ons bij elk van onze interventies.",
    produitsVertsTitle: "Groene Producten",
    produitsVertsDesc: "Wij vervangen geleidelijk onze producten door milieuvriendelijke alternatieven.",
    gestionRaisonnee: "Rationeel Beheer",
    gestionRaisonneeDesc: "Optimalisatie van middelen en afvalvermindering voor minimale milieu-impact.",
    engagementDurable: "Duurzaam Engagement",
    engagementDurableDesc: "Langetermijnvisie om bij te dragen aan een schonere en duurzamere toekomst.",
    engagementsConcrets: "Onze concrete engagementen",
    engagementsConcretsDesc: "Meetbare acties voor echte impact",
    equipementsEconomes: "Energiezuinige Apparatuur",
    equipementsEconomesDesc: "Apparatuur met laag energieverbruik",
    gestionDechets: "Afvalbeheer",
    gestionDechetsDesc: "Selectieve sortering en systematische recycling",
    formationContinue: "Continue Opleiding",
    formationContinueDesc: "Personeel opgeleid in duurzame praktijken",
    responsabiliteSociale: "Sociale verantwoordelijkheid",
    engagementsSociaux: "Onze sociale engagementen",
    engagementsSociauxDesc: "Een eerlijk en menselijk bedrijf ten dienste van zijn medewerkers en de samenleving",
    entrepriseJuste: "Eerlijk en Menselijk Bedrijf",
    entrepriseJusteDesc: "Strijd tegen discriminatie, gelijke kansen, respect en vriendelijkheid tegenover al onze medewerkers.",
    integrite: "Integriteit en Transparantie",
    integriteDesc: "Totale afwijzing van corruptie, afpersing en fraude. Respect voor wetten en professionele ethiek.",
    concurrenceLoyale: "Eerlijke Concurrentie",
    concurrenceLoyaleDesc: "Respect voor marktregels en eerlijkheid in al onze zakelijke relaties.",
    engagementSocietal: "Maatschappelijk Engagement",
    engagementSocietalDesc: "Actieve bijdrage aan de lokale en sociale ontwikkeling van onze gemeenschappen.",
    confidentialite: "Vertrouwelijkheid en Gegevensbescherming",
    confidentialiteDesc: "De veiligheid van aan ons toevertrouwde informatie is een absolute prioriteit.",
    proprieteIntellectuelle: "Respect voor Intellectueel Eigendom",
    proprieteIntellectuelleDesc: "Wij beschermen de knowhow, innovaties en professionele documenten van onze klanten en partners.",
    rejoignezDemarche: "Sluit u aan bij onze verantwoorde aanpak",
    rejoignezDemarcheDesc: "Kiezen voor Nettis betekent kiezen voor een partner die zich inzet voor een schonere en duurzamere toekomst.",
    // About Page
    aProposTitle: "Uw partner voor",
    qualiteEt: "kwaliteit",
    fiabilite: "betrouwbaarheid",
    aProposDesc: "Nettis Facility is een Belgisch bedrijf gespecialiseerd in professionele schoonmaakdiensten.",
    annees: "Jaren",
    clients: "Klanten",
    satisfaction: "Klanttevredenheid",
    notreVision: "Onze visie",
    redefinirStandards: "Netheid standaarden herdefiniëren",
    missionDesc: "Onze missie is om een schone, gezonde en aangename omgeving te garanderen voor al onze klanten.",
    notreMission: "Onze Missie",
    notreMissionDesc: "Schoonmaakdiensten van superieure kwaliteit bieden met behoud van het milieu.",
    notreVisionTitle: "Onze Visie",
    notreVisionDesc: "De referentie worden in België voor milieuvriendelijke schoonmaakdiensten.",
    nosAtouts: "Onze troeven",
    pourquoiNousChoisir: "Waarom voor ons kiezen?",
    atout1: "Meer dan 15 jaar expertise in de sector",
    atout2: "Opgeleide en gecertificeerde teams",
    atout3: "Toegewijde contactpersoon voor elke klant",
    atout4: "Gratis en transparante offertes",
    atout5: "Landelijke dekking in België",
    atout6: "Kwaliteit en MVO engagement",
    notreApproche: "Onze aanpak",
    notreMethodologie: "Onze methodologie",
    methodologieDesc: "Een bewezen proces om uw tevredenheid bij elke stap te garanderen",
    etape1: "Eerste contact",
    etape1Desc: "Eerste uitwisseling om uw specifieke behoeften en verwachtingen te begrijpen.",
    etape2: "Behoeftenanalyse",
    etape2Desc: "Bezoek aan uw locatie en nauwkeurige evaluatie van de benodigde diensten.",
    etape3: "Gepersonaliseerd voorstel",
    etape3Desc: "Gedetailleerde en transparante offerte, aangepast aan uw budget en eisen.",
    etape4: "Implementatie",
    etape4Desc: "Inzet van onze teams met een toegewijde contactpersoon tot uw dienst.",
    etape5: "Kwaliteitsmonitoring",
    etape5Desc: "Regelmatige controles en aanpassingen om uw tevredenheid te garanderen.",
    zoneIntervention: "Interventiegebied",
    couvertureNationale: "Landelijke dekking in België",
    couvertureDesc: "Gevestigd in Brussel, zijn we actief in heel België.",
    bruxellesCapitale: "Brussel-Hoofdstad",
    bruxellesDesc: "Hoofdkantoor en sterke aanwezigheid",
    wallonie: "Wallonië",
    wallonieDesc: "Volledige dekking van de regio",
    flandre: "Vlaanderen",
    flandreDesc: "Interventies op aanvraag",
    nosCoordonnees: "Onze contactgegevens",
    adresse: "Adres",
    telephone: "Telefoon",
    email: "E-mail",
    pretTravailler: "Klaar om met ons te werken?",
    pretTravaillerDesc: "Neem contact met ons op om uw professionele schoonmaakbehoeften te bespreken.",
    // Contact Page
    contactTitle: "Neem",
    nous: "contact op",
    contactDesc: "Een vraag, een project? Ons team staat tot uw beschikking om u te helpen met al uw professionele schoonmaakbehoeften.",
    envoyezMessage: "Stuur ons een bericht",
    nomComplet: "Volledige naam",
    votreMail: "uw@email.com",
    votreMessage: "Hoe kunnen we u helpen?",
    message: "Bericht",
    envoyerMessage: "Bericht verzenden",
    envoiEnCours: "Verzenden...",
    messageEnvoye: "Bericht verzonden!",
    merciContact: "Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.",
    envoyerAutre: "Nog een bericht sturen",
    champsObligatoires: "Verplichte velden",
    contactParDepartement: "Contact per afdeling",
    serviceComptable: "Boekhoudafdeling",
    ressourcesHumaines: "Human Resources",
    serviceCommercial: "Verkoopafdeling",
    rejoindreEquipeQuestion: "Wilt u lid worden van ons team?",
    rejoindreEquipeDesc: "We zijn altijd op zoek naar gemotiveerde talenten om onze teams te versterken.",
    postulerChezNettis: "Solliciteren bij Nettis",
    // Postuler Page
    rejoignezNous: "Word lid van ons",
    postulerTitle: "Solliciteer bij",
    postulerDesc: "Word lid van een dynamisch en menselijk bedrijf. We zijn altijd op zoek naar gemotiveerde talenten om onze teams te versterken.",
    horairesFlexibles: "Flexibele uren",
    horairesFlexiblesDesc: "Aangepast aan uw persoonlijke leven",
    formationContinueAvantage: "Continue opleiding",
    formationContinueAvantageDesc: "Ontwikkel uw vaardigheden",
    equipeBienveillante: "Zorgzaam team",
    equipeBienveillanteDesc: "Een menselijke omgeving",
    proximite: "Nabijheid",
    proximiteDesc: "Posities in heel België",
    formulaireCandidature: "Sollicitatieformulier",
    formulaireDesc: "Vul het onderstaande formulier in om te solliciteren bij Nettis Facility.",
    nom: "Achternaam",
    prenom: "Voornaam",
    votreNom: "Uw achternaam",
    votrePrenom: "Uw voornaam",
    votreMessageCandidature: "Stel uzelf kort voor, vermeld de gewenste functie, uw ervaring...",
    documentsOptionnels: "Documenten (optioneel)",
    formatsAcceptes: "Geaccepteerde formaten: PDF, JPG, PNG, DOC, DOCX (max. 5 MB per bestand)",
    cv: "CV",
    lettreMotivation: "Motivatiebrief",
    carteIdentite: "Identiteitskaart (voor/achter)",
    photoIdentite: "Pasfoto",
    planActiva: "Activa Plan (indien van toepassing)",
    choisirFichier: "Kies een bestand",
    supprimer: "Verwijderen",
    protectionDonnees: "Gegevensbescherming (GDPR)",
    responsableTraitement: "Verwerkingsverantwoordelijke",
    finalite: "Doel",
    finaliteDesc: "Uw gegevens worden uitsluitend verzameld voor het wervingsproces.",
    conservation: "Bewaring",
    conservationDesc: "Uw gegevens worden maximaal 2 jaar na uw laatste sollicitatie bewaard, in overeenstemming met de Belgische wetgeving.",
    vosDroits: "Uw rechten",
    vosDroitsDesc: "U heeft het recht op toegang, rectificatie, verwijdering, beperking en overdraagbaarheid van uw gegevens.",
    reclamation: "Klacht",
    reclamationDesc: "U kunt een klacht indienen bij de Gegevensbeschermingsautoriteit (GBA)",
    consentementRgpd: "Ik heb het privacybeleid gelezen en accepteer het. Ik stem in met de verwerking van mijn persoonsgegevens en documenten in het kader van mijn sollicitatie bij Nettis Facility.",
    envoyerCandidature: "Mijn sollicitatie versturen",
    candidatureEnvoyee: "Sollicitatie verzonden!",
    merciCandidature: "Bedankt voor uw interesse. Ons HR-team zal uw sollicitatie beoordelen en zo snel mogelijk contact met u opnemen.",
    soumettreAutre: "Nog een sollicitatie indienen",
    questionRecrutement: "Vragen over werving?",
    equipeRhDisposition: "Ons HR-team staat tot uw beschikking om al uw vragen te beantwoorden.",
    // Services Page - Features
    serviceFeature1_1: "Dagelijkse of wekelijkse schoonmaak",
    serviceFeature1_2: "Desinfectie van contactoppervlakken",
    serviceFeature1_3: "Vloer- en tapijtonderhoud",
    serviceFeature1_4: "Prullenbakken legen en recyclen",
    serviceFeature1_5: "Sanitair schoonmaken",
    serviceFeature2_1: "Schoonmaak van productiezones",
    serviceFeature2_2: "Ontvetten van apparatuur",
    serviceFeature2_3: "Industrieel vloeronderhoud",
    serviceFeature2_4: "Hogedrukreiniging",
    serviceFeature2_5: "Industrieel afvalbeheer",
    serviceFeature3_1: "Onderhoud van inkomhallen",
    serviceFeature3_2: "Schoonmaak van trappen en liften",
    serviceFeature3_3: "Onderhoud van buitenruimtes",
    serviceFeature3_4: "Ophalen en terugzetten van vuilnisbakken",
    serviceFeature3_5: "Onderhoud van technische ruimtes",
    serviceFeature4_1: "Verwijdering van puin en afval",
    serviceFeature4_2: "Fijnstof schoonmaak",
    serviceFeature4_3: "Oppervlakteherstel",
    serviceFeature4_4: "Volledige desinfectie",
    serviceFeature4_5: "Geurbehandeling",
    serviceFeature5_1: "Binnen- en buitenschoonmaak",
    serviceFeature5_2: "Veilig werken op hoogte",
    serviceFeature5_3: "Anti-kalkbehandeling",
    serviceFeature5_4: "Kozijnen schoonmaken",
    serviceFeature5_5: "Reguliere onderhoudscontracten",
    serviceFeature6_1: "Virucide en bactericide desinfectie",
    serviceFeature6_2: "Speciale vloerbehandeling",
    serviceFeature6_3: "Schoonmaak bij einde huur",
    serviceFeature6_4: "Tapijt shamponeren",
    serviceFeature6_5: "Marmer kristallisatie",
    // RSE Page - Social Commitment Points
    rsePoint1_1: "Gendergelijkheid op de werkvloer",
    rsePoint1_2: "Inclusie van alle achtergronden",
    rsePoint1_3: "Erkenning van verdienste",
    rsePoint1_4: "Continue opleiding",
    rsePoint2_1: "Ethische zakelijke praktijken",
    rsePoint2_2: "Prijstransparantie",
    rsePoint2_3: "Eerlijke communicatie",
    rsePoint2_4: "Respect voor engagementen",
    rsePoint3_1: "Eerlijke en transparante prijzen",
    rsePoint3_2: "Respect voor partners",
    rsePoint3_3: "Eerlijke relaties",
    rsePoint3_4: "Zakelijke ethiek",
    rsePoint4_1: "Bevordering van lokale werkgelegenheid",
    rsePoint4_2: "Verantwoorde partnerschappen",
    rsePoint4_3: "Steun aan lokale initiatieven",
    rsePoint4_4: "Professionele integratie",
    // RSE Page - Confidentiality Points
    rseConfid1: "Bescherming van persoonsgegevens",
    rseConfid2: "Vertrouwelijkheidsclausules",
    rseConfid3: "Toegangsbeveiliging",
    rseConfid4: "Personeelsopleiding",
    // RSE Page - Intellectual Property Points
    rsePropInt1: "Bescherming van innovaties",
    rsePropInt2: "Respect voor patenten",
    rsePropInt3: "Vertrouwelijkheid van processen",
    rsePropInt4: "Professionele ethiek",
    // Cookie Consent
    cookieTitle: "Wij gebruiken cookies",
    cookieDescription: "Wij gebruiken cookies om uw ervaring op onze site te verbeteren en ons verkeer te analyseren. U kunt ervoor kiezen om alle cookies te accepteren of uw voorkeuren aan te passen.",
    cookieSettings: "Cookie-instellingen",
    cookieNecessary: "Noodzakelijke cookies",
    cookieNecessaryDesc: "Deze cookies zijn essentieel voor het functioneren van de website.",
    cookieAnalytics: "Analytische cookies",
    cookieAnalyticsDesc: "Deze cookies helpen ons te begrijpen hoe u onze site gebruikt.",
    cookieAlwaysActive: "Altijd actief",
    cookieAcceptAll: "Alles accepteren",
    cookieRejectNonEssential: "Niet-essentiële weigeren",
    cookieCustomize: "Aanpassen",
    cookieSavePreferences: "Opslaan"
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  const t = useCallback((key) => {
    return translations[language][key] || translations['fr'][key] || key;
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    if (['fr', 'en', 'nl'].includes(lang)) {
      setLanguage(lang);
    }
  }, []);

  const value = useMemo(() => ({ language, changeLanguage, t }), [language, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
